import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";

const EMPTY_FORM = {
  company_name: "",
  position: "",
  name: "",
  mobile: "",
  phone: "",
  email: "",
  address: "",
  industry: "",
};

function ContactFormModal({
  title,
  form,
  onChange,
  onSubmit,
  onClose,
  error,
  submitLabel,
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-[640px] max-w-[90vw] max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-[12px]">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.company_name}
                onChange={(e) => onChange("company_name", e.target.value)}
                className="w-full p-2 border rounded text-[12px]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry
              </label>
              <input
                type="text"
                value={form.industry}
                onChange={(e) => onChange("industry", e.target.value)}
                className="w-full p-2 border rounded text-[12px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position
              </label>
              <input
                type="text"
                value={form.position}
                onChange={(e) => onChange("position", e.target.value)}
                className="w-full p-2 border rounded text-[12px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => onChange("name", e.target.value)}
                className="w-full p-2 border rounded text-[12px]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile
              </label>
              <input
                type="text"
                value={form.mobile}
                onChange={(e) => onChange("mobile", e.target.value)}
                className="w-full p-2 border rounded text-[12px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => onChange("phone", e.target.value)}
                className="w-full p-2 border rounded text-[12px]"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => onChange("email", e.target.value)}
                className="w-full p-2 border rounded text-[12px]"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => onChange("address", e.target.value)}
                className="w-full p-2 border rounded text-[12px]"
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400 text-[12px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-white px-4 py-2 rounded text-[12px]"
              style={{ backgroundColor: "#6FA944" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#5C8E37")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#6FA944")}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ConContacts = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [userRole, setUserRole] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("company_name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [deletingContact, setDeletingContact] = useState(null);
  const [formError, setFormError] = useState("");
  const [deleteError, setDeleteError] = useState("");

  const fetchContacts = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const response = await fetch(
        `/api/contacts?projectId=${projectId}&sortBy=${sortBy}&order=${sortOrder}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch contacts");
      }
      const data = await response.json();
      setContacts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, sortBy, sortOrder]);

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (!role) {
      navigate("/");
      return;
    }
    setUserRole(role);
    fetchContacts();
  }, [navigate, fetchContacts]);

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetFormState = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setEditingId(null);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    try {
      const response = await fetch("/api/contacts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          ...form,
          created_by: localStorage.getItem("userId") || "",
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to add contact");
      }
      setShowAddModal(false);
      resetFormState();
      fetchContacts();
    } catch (error) {
      setFormError(error.message);
    }
  };

  const openEditModal = (contact) => {
    setEditingId(contact._id);
    setForm({
      company_name: contact.company_name || "",
      position: contact.position || "",
      name: contact.name || "",
      mobile: contact.mobile || "",
      phone: contact.phone || "",
      email: contact.email || "",
      address: contact.address || "",
      industry: contact.industry || "",
    });
    setFormError("");
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    try {
      const response = await fetch(`/api/contacts/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to update contact");
      }
      setShowEditModal(false);
      resetFormState();
      fetchContacts();
    } catch (error) {
      setFormError(error.message);
    }
  };

  const openDeleteModal = (contact) => {
    setDeletingContact(contact);
    setDeleteError("");
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deletingContact) return;
    setDeleteError("");
    try {
      const response = await fetch(`/api/contacts/${deletingContact._id}`, {
        method: "DELETE",
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to delete contact");
      }
      setShowDeleteModal(false);
      setDeletingContact(null);
      fetchContacts();
    } catch (error) {
      setDeleteError(error.message);
    }
  };

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const sortIndicator = (column) => {
    if (sortBy !== column) return "";
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  return (
    <div className="flex h-screen">
      <Sidebar userRole={userRole} projectId={projectId} scenario="construction" />

      <div className="flex-1 bg-gray-100 p-6 ml-[60px] overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">Contacts</h2>
          <img src="/images/gemterra-logo-20260112-t.png" alt="Gemterra" className="h-6" />
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-4">
          <button
            type="button"
            onClick={() => {
              resetFormState();
              setShowAddModal(true);
            }}
            className="text-white px-4 py-2 rounded text-[12px]"
            style={{ backgroundColor: "#6FA944" }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#5C8E37")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#6FA944")}
          >
            Add Contact
          </button>

          <div className="flex items-center gap-2 text-[12px] text-gray-600">
            <span>Sort by:</span>
            <button
              type="button"
              onClick={() => toggleSort("company_name")}
              className={`px-3 py-1 rounded border ${
                sortBy === "company_name"
                  ? "bg-[#1e354a] text-white border-[#1e354a]"
                  : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              Company{sortIndicator("company_name")}
            </button>
            <button
              type="button"
              onClick={() => toggleSort("industry")}
              className={`px-3 py-1 rounded border ${
                sortBy === "industry"
                  ? "bg-[#1e354a] text-white border-[#1e354a]"
                  : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              Industry{sortIndicator("industry")}
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-600 text-[12px]">Loading contacts...</p>
        ) : contacts.length === 0 ? (
          <p className="text-gray-600 text-[12px]">No contacts yet. Click Add Contact to create one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full bg-white border border-gray-300 shadow-md rounded-lg text-[12px]">
              <thead>
                <tr className="bg-gray-200 text-gray-700">
                  <th className="p-3 text-left">Company</th>
                  <th className="p-3 text-left">Industry</th>
                  <th className="p-3 text-left">Position</th>
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Mobile</th>
                  <th className="p-3 text-left">Phone</th>
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-left">Address</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact._id} className="border-t border-gray-300 hover:bg-gray-50">
                    <td className="p-3">{contact.company_name}</td>
                    <td className="p-3">{contact.industry || "—"}</td>
                    <td className="p-3">{contact.position || "—"}</td>
                    <td className="p-3">{contact.name}</td>
                    <td className="p-3">{contact.mobile || "—"}</td>
                    <td className="p-3">{contact.phone || "—"}</td>
                    <td className="p-3">{contact.email || "—"}</td>
                    <td className="p-3">{contact.address || "—"}</td>
                    <td className="p-3 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openEditModal(contact)}
                        className="text-blue-600 hover:underline mr-3"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => openDeleteModal(contact)}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <ContactFormModal
          title="Add Contact"
          form={form}
          onChange={handleFormChange}
          onSubmit={handleAddSubmit}
          onClose={() => {
            setShowAddModal(false);
            resetFormState();
          }}
          error={formError}
          submitLabel="Save"
        />
      )}

      {showEditModal && (
        <ContactFormModal
          title="Edit Contact"
          form={form}
          onChange={handleFormChange}
          onSubmit={handleEditSubmit}
          onClose={() => {
            setShowEditModal(false);
            resetFormState();
          }}
          error={formError}
          submitLabel="Update"
        />
      )}

      {showDeleteModal && deletingContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-[420px] max-w-[90vw]">
            <h3 className="text-lg font-semibold mb-3">Delete Contact</h3>
            <p className="text-[12px] text-gray-700 mb-4">
              Are you sure you want to delete{" "}
              <strong>{deletingContact.name}</strong> from{" "}
              <strong>{deletingContact.company_name}</strong>?
            </p>
            {deleteError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-[12px]">
                {deleteError}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingContact(null);
                  setDeleteError("");
                }}
                className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400 text-[12px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-[12px]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConContacts;
